import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown, Layers, Database, Package, ShoppingCart, TrendingUp, Network, Store, Factory, Shirt, DollarSign, BookOpen, Users, Briefcase, Lightbulb, ArrowRight } from "lucide-react";
import ommniLogo from "@/assets/ommni-logo.png";
import { SearchBar } from "@/components/SearchBar";
import { trackCTAClick, trackNavigation } from "@/lib/analytics";
import DemoBookingDialog from "./DemoBookingDialog";

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);

  const productsMenu = {
    title: "Products",
    sections: [
      {
        title: "Core Platform",
        items: [
          { name: "Ommni Flow", path: "/features/ommni-flow", icon: Layers, description: "Orchestrate your entire commerce operation" },
          { name: "PIM", path: "/features/pim", icon: Database, description: "Product information management" },
          { name: "DAM", path: "/features/dam", icon: Package, description: "Digital asset management" },
          { name: "DOM", path: "/features/dom", icon: ShoppingCart, description: "Distributed order management" },
        ]
      },
      {
        title: "Capabilities",
        items: [
          { name: "Inventory", path: "/features/inventory", icon: TrendingUp, description: "Real-time inventory management" },
          { name: "Offers", path: "/features/offers", icon: DollarSign, description: "Promotions & pricing engine" },
          { name: "Integrations", path: "/integrations", icon: Network, description: "100+ platform connections" },
        ]
      }
    ]
  };

  const solutionsMenu = {
    title: "Solutions",
    sections: [
      {
        title: "By Industry",
        items: [
          { name: "Ecommerce", path: "/solutions/ecommerce", icon: Store, description: "Online retail & DTC brands" },
          { name: "Fashion", path: "/solutions/fashion", icon: Shirt, description: "Apparel & lifestyle brands" },
          { name: "Manufacturing", path: "/solutions/manufacturing", icon: Factory, description: "B2B & industrial" },
          { name: "Wholesale", path: "/solutions/wholesale", icon: Package, description: "Distribution & wholesale" },
        ]
      }
    ]
  };

  const resourcesMenu = {
    title: "Resources",
    sections: [
      {
        title: "Learn",
        items: [
          { name: "Blog", path: "/blog", icon: BookOpen, description: "Latest insights & updates" },
          { name: "Roadmap", path: "/roadmap", icon: Lightbulb, description: "See what's coming next" },
          { name: "Support", path: "/support", icon: Users, description: "Help center & documentation" },
        ]
      },
      {
        title: "Company",
        items: [
          { name: "About", path: "/about", icon: Users, description: "Our story & mission" },
          { name: "Careers", path: "/careers", icon: Briefcase, description: "Join our team" },
        ]
      }
    ]
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg" aria-label="Main navigation">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center" aria-label="Ommni home page">
            <img 
              src={ommniLogo} 
              alt="Ommni commerce operations platform logo" 
              className="h-8 w-auto"
              width="120"
              height="32"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8" role="menubar">
            {/* Products Mega Menu */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveMenu('products')}
              onMouseLeave={() => setActiveMenu(null)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors outline-none py-2">
                Products
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {activeMenu === 'products' && (
                <div className="absolute top-full left-0 pt-2 z-[100]">
                  <div className="bg-background border border-border rounded-lg shadow-2xl p-6 w-[600px]">
                    <div className="grid grid-cols-2 gap-6">
                      {productsMenu.sections.map((section, idx) => (
                        <div key={idx}>
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{section.title}</h3>
                          <div className="space-y-1">
                            {section.items.map((item) => {
                              const Icon = item.icon;
                              return (
                              <Link
                                key={item.path}
                                to={item.path}
                                className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                                onClick={() => {
                                  setActiveMenu(null);
                                  trackNavigation(item.name, item.path);
                                }}
                              >
                                <div className="mt-0.5">
                                  <Icon className="w-5 h-5 text-primary" />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-sm group-hover:text-primary transition-colors">{item.name}</div>
                                  <div className="text-xs text-muted-foreground mt-0.5">{item.description}</div>
                                </div>
                              </Link>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 pt-6 border-t border-border">
                      <Link 
                        to="/features" 
                        className="flex items-center justify-between text-sm font-medium text-primary hover:text-primary/80 transition-colors group"
                        onClick={() => setActiveMenu(null)}
                      >
                        View all features
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Solutions Mega Menu */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveMenu('solutions')}
              onMouseLeave={() => setActiveMenu(null)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors outline-none py-2">
                Solutions
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {activeMenu === 'solutions' && (
                <div className="absolute top-full left-0 pt-2 z-[100]">
                  <div className="bg-background border border-border rounded-lg shadow-2xl p-6 w-[400px]">
                    {solutionsMenu.sections.map((section, idx) => (
                      <div key={idx}>
                        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{section.title}</h3>
                        <div className="space-y-1">
                          {section.items.map((item) => {
                            const Icon = item.icon;
                            return (
                              <Link
                                key={item.path}
                                to={item.path}
                                className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                                onClick={() => {
                                  setActiveMenu(null);
                                  trackNavigation(item.name, item.path);
                                }}
                              >
                                <div className="mt-0.5">
                                  <Icon className="w-5 h-5 text-primary" />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-sm group-hover:text-primary transition-colors">{item.name}</div>
                                  <div className="text-xs text-muted-foreground mt-0.5">{item.description}</div>
                                </div>
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Resources Mega Menu */}
            <div 
              className="relative"
              onMouseEnter={() => setActiveMenu('resources')}
              onMouseLeave={() => setActiveMenu(null)}
            >
              <button className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary transition-colors outline-none py-2">
                Resources
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {activeMenu === 'resources' && (
                <div className="absolute top-full left-0 pt-2 z-[100]">
                  <div className="bg-background border border-border rounded-lg shadow-2xl p-6 w-[500px]">
                    <div className="grid grid-cols-2 gap-6">
                      {resourcesMenu.sections.map((section, idx) => (
                        <div key={idx}>
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">{section.title}</h3>
                          <div className="space-y-1">
                            {section.items.map((item) => {
                              const Icon = item.icon;
                              return (
                                <Link
                                  key={item.path}
                                  to={item.path}
                                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors group"
                                  onClick={() => setActiveMenu(null)}
                                >
                                  <div className="mt-0.5">
                                    <Icon className="w-5 h-5 text-primary" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-sm group-hover:text-primary transition-colors">{item.name}</div>
                                    <div className="text-xs text-muted-foreground mt-0.5">{item.description}</div>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Direct Link */}
            <Link
              to="/pricing"
              className="text-sm font-medium text-foreground hover:text-primary transition-colors py-2"
            >
              Pricing
            </Link>
          </div>

          <div className="hidden lg:flex items-center space-x-4">
            <SearchBar />
            <Button variant="ghost" size="sm" asChild>
              <a 
                href="https://app.ommni.io" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackCTAClick('Login', 'navigation')}
              >
                Login
              </a>
            </Button>
            <Button variant="cta" size="sm" asChild>
              <Link 
                to="/free-trial"
                onClick={() => trackCTAClick('Get Started', 'navigation')}
              >
                Get Started
              </Link>
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
          >
            {isOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div id="mobile-menu" className="lg:hidden py-4 space-y-4 max-h-[calc(100vh-5rem)] overflow-y-auto" role="menu">
            <SearchBar onClose={() => setIsOpen(false)} />
            
            {/* Products Section */}
            <div className="space-y-2">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider py-2">Products</div>
              <div className="space-y-1">
                {productsMenu.sections.map((section) => (
                  <div key={section.title} className="space-y-1">
                    <div className="text-xs text-muted-foreground pl-2 py-1">{section.title}</div>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center gap-3 px-2 py-2 rounded hover:bg-muted/50 transition-colors"
                          onClick={() => setIsOpen(false)}
                        >
                          <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium">{item.name}</div>
                            <div className="text-xs text-muted-foreground">{item.description}</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Solutions Section */}
            <div className="space-y-2 pt-4 border-t border-border">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider py-2">Solutions</div>
              <div className="space-y-1">
                {solutionsMenu.sections.map((section) => (
                  <div key={section.title} className="space-y-1">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center gap-3 px-2 py-2 rounded hover:bg-muted/50 transition-colors"
                          onClick={() => setIsOpen(false)}
                        >
                          <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium">{item.name}</div>
                            <div className="text-xs text-muted-foreground">{item.description}</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Resources Section */}
            <div className="space-y-2 pt-4 border-t border-border">
              <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider py-2">Resources</div>
              <div className="space-y-1">
                {resourcesMenu.sections.map((section) => (
                  <div key={section.title} className="space-y-1">
                    <div className="text-xs text-muted-foreground pl-2 py-1">{section.title}</div>
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center gap-3 px-2 py-2 rounded hover:bg-muted/50 transition-colors"
                          onClick={() => setIsOpen(false)}
                        >
                          <Icon className="w-4 h-4 text-primary flex-shrink-0" />
                          <div>
                            <div className="text-sm font-medium">{item.name}</div>
                            <div className="text-xs text-muted-foreground">{item.description}</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing */}
            <div className="pt-4 border-t border-border">
              <Link
                to="/pricing"
                className="block text-sm font-medium hover:text-primary transition-colors py-2"
                onClick={() => setIsOpen(false)}
              >
                Pricing
              </Link>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col space-y-2 pt-4 border-t border-border">
              <Button variant="ghost" size="sm" asChild>
                <a 
                  href="https://app.ommni.io" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={() => trackCTAClick('Login', 'mobile_navigation')}
                >
                  Login
                </a>
              </Button>
              <Button variant="cta" size="sm" asChild>
                <Link 
                  to="/free-trial" 
                  onClick={() => {
                    setIsOpen(false);
                    trackCTAClick('Get Started', 'mobile_navigation');
                  }}
                >
                  Get Started
                </Link>
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
